import { Button, Input, Popconfirm, Select, Space, Table, message } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EntityForm } from "../../components/EntityForm";
import { useEffect, useState } from "react";

import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { getProject, getProjects } from "../../api/project";
import { UserRoles } from "../user";
import dayjs from "dayjs";
import { useTaskSection } from "./useTaskSection";

export function Task({ user }) {
  const navigate = useNavigate();
  let [header, setHeader] = useState({
    title: "Tasks",
    subTitle: "List of tasks in company",
  });

  let [searchParams] = useSearchParams();
  const [searchProjectId, setSearchProjectId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);

  const [showCreateTask, setShowCreateTask] = useState(false);
  const [editTask, setEditTask] = useState(null);

  const {
    tasks,
    sections,
    isLoading,
    searchProject,
    setSearchProject,
    handleDelete,
    handleSubmit,
    pagination,
    fetchTasks,
  } = useTaskSection({
    user,
    attrs: editTask,
    isEdit: !!editTask,
  });

  useEffect(() => {
    if (!user) return;

    if (searchParams.get("projectId") && searchProject.length === 0) {
      getProject(searchParams.get("projectId"))
        .then((project) => {
          setSearchProject([project]);
          setHeader({
            title: `${project.name}`,
            subTitle: `List of tasks in project ${project.name}`,
          });
        })
        .catch(console.error);
    }

    fetchTasks({
      page: pagination.page,
      perPage: pagination.perPage,
      q: searchQuery,
      projectId: searchProjectId || searchParams.get("projectId"),
      assigneeId: user.role === UserRoles.Employee ? user.id : null,
    });
  }, [user, pagination.page, searchQuery, searchProjectId, searchParams]);

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Assignee", dataIndex: "assigneeName", key: "assigneeName" },
    { title: "Project", dataIndex: "projName", key: "projName" },
    { title: "Start Date", dataIndex: "startDate", key: "startDate" },
    { title: "Due Date", dataIndex: "dueDate", key: "dueDate" },
    {
      title: "Action",
      key: "action",
      with: 300,
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this task?"
            onConfirm={() => handleDelete(tasks[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setEditTask(tasks[index])}
          ></Button>
          <Button
            onClick={() => navigate(`/tasks/${tasks[index].id}/work-log`)}
          >
            Manage work logs
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <PageHeader
          onBack={() => navigate(-1)}
          title={header.title}
          subTitle={header.subTitle}
        />

        {user.role !== UserRoles.Employee && (
          <Button type="primary" onClick={() => setShowCreateTask(true)}>
            Create Task
          </Button>
        )}
      </Space>

      <Space>
        <Input.Search
          className="search"
          placeholder="Name of task"
          onSearch={(str) => {
            setSearchQuery(str);
            pagination.setPage(1);
          }}
        />

        {!searchParams.get("projectId") && (
          <Select
            placeholder="Search Project"
            onSearch={async (str) => {
              const { results: projects } = await getProjects({ q: str });
              setSearchProject(projects);
            }}
            options={searchProject.map((project) => {
              return {
                value: project.id,
                label: project.name,
              };
            })}
            defaultActiveFirstOption={false}
            showArrow={false}
            filterOption={false}
            onChange={(projId) => setSearchProjectId(projId)}
            showSearch
            style={{ width: 200 }}
          />
        )}
      </Space>

      <Table
        dataSource={tasks.map((task, index) => {
          return {
            no: pagination.dataIndex + index + 1,
            ...task,
            assigneeName:
              task.assignee.firstName + " " + (task.assignee.lastName || ""),
            projName: task.project.name,
            startDate: task.startDate
              ? dayjs(task.startDate).format("YYYY-MM-DD HH:mm a")
              : null,
            dueDate: task.startDate
              ? dayjs(task.dueDate).format("YYYY-MM-DD HH:mm a")
              : null,
          };
        })}
        columns={TableCols}
        loading={isLoading}
        pagination={{
          size: "small",
          total: pagination.total,
          pageSize: 5,
          current: parseInt(pagination.page),
          onChange: pagination.setPage,
          showQuickJumper: true,
          showTotal: () => `Total ${pagination.total} tasks`,
          position: ["bottomLeft"],
        }}
      />

      {(showCreateTask || editTask) && (
        <EntityForm
          initialValues={{
            ...editTask,
            assigneeId: editTask?.assignee?.id,
            equipmentId: editTask?.equipment?.id,
            projectId: editTask?.project?.id,
            startDate: editTask?.startDate
              ? dayjs(editTask.startDate, "YYYY-MM-DD HH:mm aZ[Z]")
              : null,
            dueDate: editTask?.dueDate
              ? dayjs(editTask.dueDate, "YYYY-MM-DD HH:mm aZ[Z]")
              : null,
          }}
          sections={sections}
          title="Task"
          open={showCreateTask || editTask}
          onCancel={() => {
            setShowCreateTask(false);
            setEditTask(null);
          }}
          onSubmit={(...args) =>
            handleSubmit(...args)
              .then(() => {
                setEditTask(null);
                setShowCreateTask(false);
              })
              .catch((err) => {
                console.error(err);
                message.error("Something went wrong");
              })
          }
          onDelete={() => {
            handleDelete(editTask).then(() => setEditTask(null));
          }}
          isEditMode={!!editTask}
        ></EntityForm>
      )}
    </Space>
  );
}
